import {openDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {head, isNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  DmsDataGrid,
  PriceListExternalFilters,
  PriceListRowData as PriceListDetailRowData,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  logError,
} from '@dms/shared';
import {PriceListDetail} from '@dms/shared-aftersales';
import {FullscreenDataGridWrapper} from '@dms/warehouse/shared-components';

import {Nullish, RequiredTestIdProps, useLatestValue} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {ItemRowData} from '../types/ItemRowData';

interface ReceiveNoteMaterialPriceListTabProps extends RequiredTestIdProps {
  warehouseId: string;
  authorizationProfileId: string | Nullish;
  branchId: string | Nullish;
  onAddToBasket: (itemRowData: ItemRowData) => void;
  hasPendingRequest: boolean;
}

const RECEIVE_NOTE_MATERIAL_EXTERNAL_FILTER_ID = 'receiveNote-material-external-filter';
const PREDEFINED_COLUMNS = [
  'id',
  'catalogNumber',
  'manufacturerId',
  'name',
  'supplierId',
  'name',
  'catalogNumber',
  'orderingNumber',
  'manufacturerId',
  'supplierId',
  'purchasePrice',
  'recommendedPrice',
  'supplierDiscountGroup',
  'supplierOrderingNumber',
  'supplierManufacturerName',
  'supplierManufacturerCode',
  'standardOrderQuantity',
  'localStock',
  'totalStock',
] as const;

export function ReceiveNoteMaterialPriceListTab(props: ReceiveNoteMaterialPriceListTabProps) {
  const hasPendingRequestRef = useLatestValue(props.hasPendingRequest);

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([
      filter,
      {branchId: props.branchId, authorizationProfileId: props.authorizationProfileId},
    ]);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    getColumnData,
    rowId,
  }) => {
    if (hasPendingRequestRef.current) {
      return showNotification.warning(
        i18n.t('entity.warehouse.labels.thereIsAnotherOngoingAction')
      );
    }

    const priceListId = isArray(rowId) ? head(rowId) : rowId;
    const itemCatalogNumber = getColumnData<string>('catalogNumber')?.value;
    const itemManufacturerId = getColumnData<{id: string}>('manufacturerId')?.value?.id;
    const itemName = getColumnData<string>('name')?.value;
    const itemSupplierId = getColumnData<{id: string}>('supplierId')?.value?.id;

    if (isNil(itemCatalogNumber)) {
      return logError('catalogNumber is not defined');
    }

    if (isNil(itemManufacturerId)) {
      return logError('manufacturerId is not defined');
    }

    if (isNil(itemName)) {
      return logError('name is not defined');
    }

    if (isNil(itemSupplierId)) {
      return logError('supplierId is not defined');
    }

    if (isNil(priceListId)) {
      return logError('priceListId was not defined');
    }

    const itemRowData: ItemRowData = {
      warehouseId: props.warehouseId,
      articleId: '', // not available on price list DG, is handled within props.onAddToBasket
      catalogNumber: itemCatalogNumber,
      name: itemName,
      manufacturerId: itemManufacturerId,
      itemId: rowId as string,
      supplierId: itemSupplierId,
    };

    const priceListRowData = {
      name: {value: getColumnData('name')?.value},
      catalogNumber: {value: getColumnData('catalogNumber')?.value},
      supplierOrderingNumber: {value: getColumnData('supplierOrderingNumber')?.value},
      manufacturerId: {value: getColumnData('manufacturerId')?.value},
      supplierId: {value: getColumnData('supplierId')?.value},
      purchasePrice: {value: getColumnData('purchasePrice')?.value},
      recommendedPrice: {value: getColumnData('recommendedPrice')?.value},
      supplierDiscountGroup: getColumnData('supplierDiscountGroup')?.value,
      supplierManufacturerName: getColumnData('supplierManufacturerName')?.value,
      supplierManufacturerCode: getColumnData('supplierManufacturerCode')?.value,
      standardOrderQuantity: getColumnData('standardOrderQuantity')?.value,
      localStock: getColumnData('localStock')?.value,
      totalStock: getColumnData('totalStock')?.value,
    } as PriceListDetailRowData;

    match(actionKey)
      .with('redirectDetail', () => {
        openDialog(
          <PriceListDetail
            id={priceListId}
            priceListRowData={priceListRowData}
            data-testid={testIds.warehouse.priceList('dialogs.detail')}
          />,
          {
            id: 'detailDialog',
            title: priceListRowData?.name?.value,
            size: 'large',
            withAdditionalFooter: true,
          }
        );
      })
      .with('addToBasket', () => {
        props.onAddToBasket(itemRowData);
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <FullscreenDataGridWrapper
      externalFilterId={RECEIVE_NOTE_MATERIAL_EXTERNAL_FILTER_ID}
      hideCardWrapper
      data-testid={props['data-testid']}
    >
      <DmsDataGrid
        gridCode={WAREHOUSE_GRID_CODES.priceListBasket}
        actionCallback={actionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        externalFilterId={RECEIVE_NOTE_MATERIAL_EXTERNAL_FILTER_ID}
        _useAsLastResort_definitionBehaviorOverrides={{filterMode: 'NATIVE'}}
        filterComponent={PriceListExternalFilters}
        queryModifier={queryModifier}
        data-testid={WAREHOUSE_GRID_CODES.priceListBasket}
      />
    </FullscreenDataGridWrapper>
  );
}
